import React, { useState, useEffect, useCallback } from 'react';
import { BellRing, Loader, AlertCircle, Check, ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react';
import { orderAlerts } from '../services/api';

// The alert list (Carman and Matt, Oct 26, 2026): every alert that needs someone to act on
// an order, in one place instead of its own email. Sorted so anything shipping today is on
// top. Clear records who and when; cleared alerts are the History tab. The 8 AM email and
// the same-day reminders point here. Server: cp-alerts.js (board / detail / clear),
// rules in netlify/functions/lib/cp-alerts.js.

const CARD = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '12px 14px', marginBottom: 10 };
const PILL = { display: 'inline-block', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999, marginRight: 8 };
const BTN = { padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: '1px solid var(--gray-300)', background: '#fff', color: 'var(--navy)' };
const RED = '#B42318';
const ORANGE = 'var(--orange, #DC582A)';

function shipsBadge(a) {
  if (!a.ships) return <span style={{ ...PILL, background: 'var(--gray-100)', color: 'var(--gray-600)' }}>No ship date</span>;
  if (a.shipsToday) return <span style={{ ...PILL, background: ORANGE, color: '#fff' }}>Ships today</span>;
  if (a.overdue) return <span style={{ ...PILL, background: '#FDECEA', color: RED }}>Ship date {a.ships} (past)</span>;
  return <span style={{ ...PILL, background: 'var(--gray-100)', color: 'var(--navy)' }}>Ships {a.ships}</span>;
}

function Detail({ id }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    orderAlerts('detail', { id }).then(setD).catch((e) => setErr(e.message));
  }, [id]);
  if (err) return <div style={{ color: RED, fontSize: 13 }}>{err}</div>;
  if (!d) return <div style={{ fontSize: 13, color: 'var(--gray-500)' }}><Loader size={13} className="spin" /> Loading the alert...</div>;
  if (!d.html) return <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>No more detail was saved for this alert.</div>;
  // sandbox="" : the alert's HTML is shown, never run.
  return (
    <iframe title={'Alert ' + id} sandbox="" srcDoc={`<base target="_blank"><body style="margin:8px;font-family:Calibri,Arial,sans-serif">${d.html}</body>`}
      style={{ width: '100%', height: 420, border: '1px solid var(--gray-200)', borderRadius: 6, background: '#fff' }} />
  );
}

export default function OrderAlertList({ navigateTo }) {
  const [view, setView] = useState('open');
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [clearing, setClearing] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    setError('');
    try { setData(await orderAlerts('board', { view, days })); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [view, days]);
  useEffect(() => { load(); }, [load]);
  // The list changes as alerts come in and others clear them: refresh every minute.
  useEffect(() => {
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  const clear = async (id) => {
    setBusy(true); setError('');
    try {
      await orderAlerts('clear', { id, note });
      setClearing(null); setNote('');
      await load(true);
    } catch (e) { setError(e.message); load(true); }
    finally { setBusy(false); }
  };

  const c = (data && data.counts) || { open: 0, shipToday: 0, overdue: 0 };
  const alerts = (data && data.alerts) || [];

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '16px 16px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <BellRing size={22} color="var(--navy)" />
        <h1 style={{ fontSize: 22, margin: 0, color: 'var(--navy)' }}>Order Alerts</h1>
        <button style={{ ...BTN, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => load()}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: '0 0 14px' }}>
        Orders that need someone to act before they ship. Clear each one once it is dealt with.
        Anything shipping today that is still open gets a reminder email as the day goes on.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <span style={{ ...PILL, fontSize: 14, background: 'var(--navy)', color: '#fff' }}>{c.open} open</span>
        <span style={{ ...PILL, fontSize: 14, background: c.shipToday ? ORANGE : 'var(--gray-100)', color: c.shipToday ? '#fff' : 'var(--gray-600)' }}>{c.shipToday} ship today</span>
        {c.overdue > 0 && <span style={{ ...PILL, fontSize: 14, background: '#FDECEA', color: RED }}>{c.overdue} past ship date</span>}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, borderBottom: '1px solid var(--gray-200)' }}>
        {[['open', 'Open'], ['history', 'History']].map(([v, l]) => (
          <button key={v} onClick={() => { setView(v); setOpen({}); setClearing(null); }}
            style={{ ...BTN, border: 'none', borderBottom: view === v ? `3px solid ${ORANGE}` : '3px solid transparent', borderRadius: 0, background: 'none', padding: '8px 14px' }}>
            {l}
          </button>
        ))}
        {view === 'history' && (
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ ...BTN, marginLeft: 'auto', padding: '5px 8px' }}>
            {[7, 14, 30, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        )}
      </div>

      {error && <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: RED, fontSize: 13, marginBottom: 10 }}><AlertCircle size={15} /> {error}</div>}
      {loading && !data && <div style={{ fontSize: 14, color: 'var(--gray-500)' }}><Loader size={15} className="spin" /> Loading...</div>}
      {data && !alerts.length && (
        <div style={{ ...CARD, textAlign: 'center', color: 'var(--gray-600)', padding: 30 }}>
          {view === 'open' ? <><Check size={22} color="#1E7B3C" /><div style={{ marginTop: 6, fontSize: 15 }}>Nothing open. All clear.</div></> : 'Nothing cleared in this period.'}
        </div>
      )}

      {alerts.map((a) => {
        const accent = view === 'open' ? (a.shipsToday ? ORANGE : a.overdue ? RED : 'var(--gray-300)') : '#1E7B3C';
        return (
          <div key={a.id} style={{ ...CARD, borderLeft: `4px solid ${accent}` }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              {shipsBadge(a)}
              {a.order && (
                <button onClick={() => navigateTo && navigateTo('order-details', String(a.order))}
                  style={{ ...BTN, border: 'none', padding: 0, fontSize: 15, fontWeight: 700, color: 'var(--navy)', textDecoration: 'underline' }}>
                  #{a.order}
                </button>
              )}
              {a.customer && <span style={{ fontSize: 14, color: 'var(--gray-700)' }}>{a.customer}</span>}
              <span style={{ fontSize: 12, color: 'var(--gray-500)', marginLeft: 'auto' }}>{a.kindLabel} · {a.raised}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>{a.wrong}</div>
            {a.todo && <div style={{ fontSize: 13, color: 'var(--gray-700)', marginBottom: 8 }}><b>What to do:</b> {a.todo}</div>}

            {view === 'history' && (
              <div style={{ fontSize: 13, color: '#1E7B3C', marginBottom: 6 }}>
                {a.status === 'expired' ? `Expired ${a.expiredAt}` : `Cleared by ${a.clearedBy} at ${a.clearedAt}`}
                {a.clearedNote ? `: ${a.clearedNote}` : ''}
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <button style={{ ...BTN, display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => setOpen({ ...open, [a.id]: !open[a.id] })}>
                {open[a.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Details
              </button>
              {view === 'open' && clearing !== a.id && (
                <button style={{ ...BTN, background: 'var(--navy)', color: '#fff', border: 'none' }} onClick={() => { setClearing(a.id); setNote(''); }}>
                  Clear
                </button>
              )}
              {view === 'open' && clearing === a.id && (
                <>
                  <input autoFocus value={note} onChange={(e) => setNote(e.target.value)} maxLength={300}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !busy) clear(a.id); if (e.key === 'Escape') setClearing(null); }}
                    placeholder="What was done (optional)"
                    style={{ flex: '1 1 220px', padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                  <button disabled={busy} style={{ ...BTN, background: '#1E7B3C', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => clear(a.id)}>
                    <Check size={14} /> {busy ? 'Clearing...' : 'Confirm clear'}
                  </button>
                  <button style={{ ...BTN, display: 'flex', alignItems: 'center' }} onClick={() => setClearing(null)} aria-label="Cancel"><X size={14} /></button>
                </>
              )}
            </div>
            {open[a.id] && <div style={{ marginTop: 10 }}><Detail id={a.id} /></div>}
          </div>
        );
      })}
    </div>
  );
}
